import React, { useState, useEffect, useRef } from 'react';
import { useGlassTypes } from '../contexts/GlassTypesContext';
import { GlassType } from '../types/glassTypes';
import { Plus, X, Database, User, Upload, Trash2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import GlassTypeModal from './GlassTypeModal';

// Local storage keys
const STORAGE_KEYS = {
  ACTIVE_VIEW: 'glassTypesManager_activeView',
  SELECTED_TYPE: 'glassTypesManager_selectedType',
  NEW_GLASS_TYPE: 'glassTypesManager_newGlassType',
  IS_CUSTOM_THICKNESS: 'glassTypesManager_isCustomThickness'
};

export const GlassTypesManager: React.FC = () => {
  const {
    glassTypes,
    customGlassTypes,
    enabledTypes,
    allUniqueTypes,
    addCustomGlassType,
    removeCustomGlassType,
    toggleEnabledType,
    selectedSource,
    availableSources,
    setSelectedSource,
    uploadCSVFile,
    removeUploadedSource,
    isUserUploadedSource,
    availableGroups,
    selectedGroup,
    setSelectedGroup
  } = useGlassTypes();

  // State to track the currently selected type
  const [selectedType, setSelectedType] = useState<string>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.SELECTED_TYPE);
    return saved || '';
  });

  // State to track if the add form is open
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  // State to track which view is active (default or custom)
  const [activeView, setActiveView] = useState<'default' | 'custom'>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.ACTIVE_VIEW);
    return (saved as 'default' | 'custom') || 'default';
  });

  // State to track selected thickness filter for Monolithic glass
  const [selectedThicknessFilter, setSelectedThicknessFilter] = useState<string>('');

  // Ref for the slide-in menu
  const menuRef = useRef<HTMLDivElement>(null);

  // State to track if custom thickness is being used
  const [isCustomThickness, setIsCustomThickness] = useState<boolean>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.IS_CUSTOM_THICKNESS);
    return saved === 'true';
  });

  // State to track the selected thickness string for display
  const [selectedThicknessStr, setSelectedThicknessStr] = useState<string>('');

  // State for the glass type modal
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedGlassType, setSelectedGlassType] = useState<GlassType | null>(null);

  // Initialize newGlassType from localStorage if available
  const [newGlassType, setNewGlassType] = useState<Omit<GlassType, 'id'>>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.NEW_GLASS_TYPE);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing saved glass type:', e);
      }
    }
    return {
      name: '',
      type: '',
      thickness: 0,
      lbsPerSqFt: 0,
      price: 0,
      source: 'custom'
    };
  });

  // Set the first enabled type as selected when component mounts if no saved selection
  useEffect(() => {
    if (enabledTypes.length > 0 && !selectedType) {
      setSelectedType(enabledTypes[0]);
    }
  }, [enabledTypes, selectedType]);

  // Save activeView to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.ACTIVE_VIEW, activeView);
  }, [activeView]);

  // Save selectedType to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.SELECTED_TYPE, selectedType);
  }, [selectedType]);

  // Save newGlassType to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.NEW_GLASS_TYPE, JSON.stringify(newGlassType));
  }, [newGlassType]);

  // Save isCustomThickness to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.IS_CUSTOM_THICKNESS, isCustomThickness.toString());
  }, [isCustomThickness]);

  // Handle clicks outside the menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Check if the click is on a dropdown menu or select item
      const isDropdownClick = !!target.closest('[role="listbox"]') ||
                             !!target.closest('[data-radix-select-content]') ||
                             !!target.closest('[data-radix-popper-content-wrapper]');

      // Only close if the click is outside the menu AND not on a dropdown
      if (menuRef.current &&
          !menuRef.current.contains(target) &&
          isMenuOpen &&
          !isDropdownClick) {
        toggleMenu();
      }
    };

    // Only add the event listener if the menu is open
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Handle keyboard events (ESC to close)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMenuOpen) {
        toggleMenu();
      }
    };

    if (isMenuOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMenuOpen]);

  // Define all possible glass types
  const ALL_GLASS_TYPES = [
    'Mirror',
    'Monolithic',
    'Laminated',
    'Wire',
    'Fire Rated',
    'Pattern',
    'Spandrel',
    'Specialty',
    'IG Commercial',
    'IG Residential'
    // 'Insulated' removed as requested - now using 'IG Commercial' instead
  ];

  // Format thickness for display
  const formatThickness = (thickness: number): string => {
    if (thickness === 0.0625) return '1/16"';
    if (thickness === 0.125) return '1/8"';
    if (thickness === 0.1875) return '3/16"';
    if (thickness === 0.25) return '1/4"';
    if (thickness === 0.375) return '3/8"';
    if (thickness === 0.5) return '1/2"';
    if (thickness === 0.5625) return '9/16"';
    if (thickness === 0.75) return '3/4"';
    if (thickness === 1) return '1"';
    if (thickness === 1.3125) return '1 5/16"';
    return `${thickness}"`;
  };

  // Helper function to check if a thickness value matches the selected filter
  const matchesThicknessFilter = (type: GlassType, filter: string): boolean => {
    // Get the thickness in various formats
    const numericThickness = type.thickness;
    const thicknessStr = type.thicknessStr || '';
    const formattedThickness = formatThickness(numericThickness);

    // Check for exact matches first
    if (thicknessStr === filter || formattedThickness === filter) {
      return true;
    }

    // Check for numeric equivalents
    if (filter === '1/8"' && (numericThickness === 0.125 || thicknessStr.includes('1/8'))) {
      return true;
    }
    if (filter === '1/4"' && (numericThickness === 0.25 || thicknessStr.includes('1/4'))) {
      return true;
    }
    if (filter === '3/8"' && (numericThickness === 0.375 || thicknessStr.includes('3/8'))) {
      return true;
    }
    if (filter === '1/2"' && (numericThickness === 0.5 || thicknessStr.includes('1/2'))) {
      return true;
    }

    // Check for variations in format
    const cleanFilter = filter.replace(/["\s]/g, ''); // Remove quotes and spaces
    const cleanThicknessStr = thicknessStr.replace(/["\s]/g, ''); // Remove quotes and spaces

    return cleanThicknessStr.includes(cleanFilter) || cleanFilter.includes(cleanThicknessStr);
  };

  // Filter glass types based on active view, enabled types, selected type, and thickness filter
  const filteredGlassTypes = React.useMemo(() => {
    // Get the appropriate glass types array based on active view
    const typesToFilter = activeView === 'default' ? glassTypes : customGlassTypes;

    // Only log once per render to avoid excessive logging
    console.log(`Filtering ${typesToFilter.length} glass types with ${enabledTypes.length} enabled types`);
    console.log('Enabled types:', enabledTypes);

    // Log all unique types in the current dataset for debugging
    const uniqueTypesInData = Array.from(new Set(typesToFilter.map(t => t.type)));
    console.log('Unique types in current dataset:', uniqueTypesInData);

    return typesToFilter.filter(type => {
      // Get the exact type string for precise matching
      const exactType = type.type;

      // Normalize the type for case-insensitive comparison
      const normalizedType = type.type.charAt(0).toUpperCase() + type.type.slice(1).toLowerCase();
      const normalizedSelectedType = selectedType.charAt(0).toUpperCase() + selectedType.slice(1).toLowerCase();

      // Normalize enabled types for comparison
      const normalizedEnabledTypes = enabledTypes.map(t =>
        t.charAt(0).toUpperCase() + t.slice(1).toLowerCase()
      );

      // First check for exact match in enabled types (case-insensitive)
      const exactMatch = normalizedEnabledTypes.some(t => t === normalizedType);

      // If we have an exact match, use that
      if (exactMatch) {
        // Check if it matches the selected type filter
        const typeMatches = selectedType === '' || normalizedType === normalizedSelectedType;

        // Apply thickness filter if we're looking at Monolithic glass and a thickness filter is selected
        if (typeMatches && normalizedSelectedType === 'Monolithic' && selectedThicknessFilter) {
          return matchesThicknessFilter(type, selectedThicknessFilter);
        }

        return typeMatches;
      }

      // Otherwise, use more lenient matching
      const isEnabled = normalizedEnabledTypes.some(enabledType => {
        // Type contains enabled type
        if (normalizedType.includes(enabledType)) return true;
        // Enabled type contains type
        if (enabledType.includes(normalizedType)) return true;
        return false;
      });

      // More precise matching for selected type
      const matchesSelected = selectedType === '' || // No filter
                             normalizedType === normalizedSelectedType || // Exact match
                             normalizedType.includes(normalizedSelectedType) || // Type contains selected
                             normalizedSelectedType.includes(normalizedType); // Selected contains type

      // Apply thickness filter if we're looking at Monolithic glass and a thickness filter is selected
      if (matchesSelected && isEnabled && normalizedSelectedType === 'Monolithic' && selectedThicknessFilter) {
        return matchesThicknessFilter(type, selectedThicknessFilter);
      }

      return isEnabled && matchesSelected;
    });
  }, [activeView, glassTypes, customGlassTypes, enabledTypes, selectedType, selectedThicknessFilter]);

  // Debug logging - only log once per render
  React.useEffect(() => {
    console.log(`Selected source: ${selectedSource}`);
    console.log(`Total glass types: ${glassTypes.length}`);
    console.log(`Filtered glass types: ${filteredGlassTypes.length}`);
    console.log(`Enabled types: ${enabledTypes.join(', ')}`);
    console.log(`Selected type: ${selectedType}`);

    // Log the first few glass types for debugging
    if (glassTypes.length > 0) {
      console.log('Sample glass types:');
      glassTypes.slice(0, 3).forEach((type, index) => {
        console.log(`Type ${index + 1}:`, type);
      });

      // Count types by category
      const typeCounts = {};
      glassTypes.forEach(type => {
        const normalizedType = type.type.charAt(0).toUpperCase() + type.type.slice(1).toLowerCase();
        typeCounts[normalizedType] = (typeCounts[normalizedType] || 0) + 1;
      });
      console.log('Glass types by category:', typeCounts);

      // Log filtered types by category
      const filteredTypeCounts = {};
      filteredGlassTypes.forEach(type => {
        const normalizedType = type.type.charAt(0).toUpperCase() + type.type.slice(1).toLowerCase();
        filteredTypeCounts[normalizedType] = (filteredTypeCounts[normalizedType] || 0) + 1;
      });
      console.log('Filtered glass types by category:', filteredTypeCounts);
    }
  }, [selectedSource, glassTypes.length, filteredGlassTypes.length, enabledTypes, selectedType]);
  if (glassTypes.length > 0) {
    console.log('Sample glass type:', glassTypes[0]);
  }

  // Thickness options for the dropdown with their corresponding numeric values
  const thicknessOptions = [
    { display: '1/16"', value: 0.0625 },
    { display: '1/8"', value: 0.125 },
    { display: '3/16"', value: 0.1875 },
    { display: '1/4"', value: 0.25 },
    { display: '3/8"', value: 0.375 },
    { display: '1/2"', value: 0.5 },
    { display: '9/16"', value: 0.5625 },
    { display: '3/4"', value: 0.75 },
    { display: '1"', value: 1 },
    { display: '1 5/16"', value: 1.3125 },
    { display: 'Other', value: 0 }
  ];

  // Format currency for display
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addCustomGlassType(newGlassType);

    // Clear form but keep the type selection for convenience
    const currentType = newGlassType.type;
    setNewGlassType({
      name: '',
      type: currentType, // Keep the type selection
      thickness: 0,
      lbsPerSqFt: 0,
      price: 0,
      source: 'custom'
    });

    // Reset the thickness string
    setSelectedThicknessStr('');

    setIsMenuOpen(false);
  };

  // Handle type selection
  const handleTypeSelect = (type: string) => {
    setSelectedType(type);
    // Clear thickness filter when changing type
    setSelectedThicknessFilter('');
  };

  // Handle thickness filter selection
  const handleThicknessSelect = (thickness: string) => {
    // Toggle thickness filter (clear if already selected, set if not)
    const newFilter = selectedThicknessFilter === thickness ? '' : thickness;
    setSelectedThicknessFilter(newFilter);

    // Debug logging for thickness filter
    if (newFilter) {
      console.log(`Applied thickness filter: ${newFilter}`);
      // Log some sample glass types with their thickness values for debugging
      const monolithicTypes = glassTypes.filter(type => {
        const normalizedType = type.type.charAt(0).toUpperCase() + type.type.slice(1).toLowerCase();
        return normalizedType === 'Monolithic';
      }).slice(0, 5);

      console.log('Sample Monolithic glass types with thickness values:');
      monolithicTypes.forEach(type => {
        console.log(`- ${type.name}: numeric=${type.thickness}, string=${type.thicknessStr || 'N/A'}, formatted=${formatThickness(type.thickness)}`);
        console.log(`  Matches filter: ${matchesThicknessFilter(type, newFilter)}`);
      });
    } else {
      console.log('Cleared thickness filter');
    }
  };

  // Handle glass type card click
  const handleGlassTypeClick = (glassType: GlassType) => {
    // Open modal for all glass types
    setSelectedGlassType(glassType);
    setIsModalOpen(true);
  };

  // Toggle the menu
  const toggleMenu = () => {
    console.log('Toggle fired, current state:', isMenuOpen);
    setIsMenuOpen(prevState => !prevState);
    // When closing the menu, switch back to default view
    if (isMenuOpen) {
      setActiveView('default');
    }
    console.log('New state will be:', !isMenuOpen);
  };

  // Toggle between default and custom views
  const toggleView = (view: 'default' | 'custom') => {
    setActiveView(view);
  };

  // Handle CSV file upload
  const handleCSVUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const success = await uploadCSVFile(file);
      if (success) {
        alert(`Successfully uploaded ${file.name}`);
      } else {
        alert(`Failed to upload ${file.name}. Please check the file format.`);
      }
    } catch (error) {
      console.error('Error uploading CSV file:', error);
      alert(`Error uploading file: ${error.message}`);
    } finally {
      // Reset the file input
      e.target.value = '';
    }
  };

  // Handle deleting a user-uploaded source
  const handleDeleteSource = (sourceName: string) => {
    if (confirm(`Are you sure you want to delete the "${sourceName}" source? This action cannot be undone.`)) {
      removeUploadedSource(sourceName);
    }
  };

  // Define the exact animation CSS classes
  const menuClasses = `
    fixed top-32 w-96 bg-[#1e293b] shadow-lg z-[100] rounded-tl-lg border-2 border-blue-500
    transform transition-all duration-300 ease-in-out
    ${isMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
  `;

  return (
    <div className="p-4 space-y-6 relative min-h-screen">
      {/* All buttons in a single container */}
      <div className="absolute top-4 right-4 flex space-x-2">
        {activeView === 'custom' && (
          <button
            onClick={toggleMenu}
            data-menu-toggle
            aria-expanded={isMenuOpen}
            aria-haspopup="true"
            disabled={isMenuOpen}
            className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1 ${
              isMenuOpen
                ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
            }`}
          >
            <Plus className="h-4 w-4" />
            Add Custom Glass Type
          </button>
        )}
        <div className="flex items-center space-x-2">
          {/* CSV Upload Button */}
          <label
            htmlFor="csv-upload"
            className="px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1 bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 cursor-pointer"
          >
            <Upload className="h-4 w-4" />
            Add CSV
            <input
              id="csv-upload"
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleCSVUpload}
            />
          </label>

          <div className="flex items-center">
            <Select
              value={selectedSource}
              onValueChange={(value) => {
                console.log(`Dropdown changed to: ${value}`);
                setSelectedSource(value);
              }}
              onOpenChange={(open) => {
                // When the select opens, add a click event listener to prevent propagation
                if (open) {
                  const selectContent = document.querySelector('[data-radix-select-content]');
                  if (selectContent) {
                    selectContent.addEventListener('click', (e) => e.stopPropagation(), { capture: true });
                  }
                }
              }}
            >
              <SelectTrigger className="w-[180px] bg-blue-600 hover:bg-blue-700 text-white border-none">
                <SelectValue placeholder="Select source">{selectedSource}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {availableSources.filter(source => source !== 'default').map((source) => (
                  <SelectItem key={source} value={source}>
                    {source}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Delete button for user-uploaded sources */}
            {isUserUploadedSource(selectedSource) && (
              <button
                onClick={() => handleDeleteSource(selectedSource)}
                className="ml-2 p-1.5 rounded-md text-sm font-medium flex items-center gap-1 bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                title="Delete this uploaded source"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}

            {/* Hidden group selection dropdown - functional but not visible */}
            <div className="hidden">
              <Select
                value={selectedGroup}
                onValueChange={(value) => {
                  console.log(`Group dropdown changed to: ${value}`);
                  setSelectedGroup(value);
                }}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select group">{selectedGroup}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {availableGroups.map((group) => (
                    <SelectItem key={group} value={group}>
                      {group}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex space-x-2">
            <Button
              variant={activeView === 'default' ? 'default' : 'outline'}
              onClick={() => setActiveView('default')}
              className={activeView === 'default' ? "bg-blue-600 hover:bg-blue-700 text-white" : "text-white"}
            >
              Default Glass Types
            </Button>
            <Button
              variant={activeView === 'custom' ? 'default' : 'outline'}
              onClick={() => setActiveView('custom')}
              className={activeView === 'custom' ? "bg-blue-600 hover:bg-blue-700 text-white" : "text-white"}
            >
              Custom Glass Types
            </Button>
          </div>
        </div>
      </div>

      {/* Slide-in Side Menu - IMPORTANT: Don't use conditional rendering with &&, use CSS transform instead */}
      <div
        ref={menuRef}
        className={menuClasses}
        role="dialog"
        aria-modal="true"
        aria-labelledby="menu-title"
        style={{
          right: isMenuOpen ? '0' : '-100%'
        }}
      >
        <div className="p-6 overflow-y-auto max-h-[calc(100vh-8rem)]">
          <div className="flex justify-between items-center mb-6">
            <h2 id="menu-title" className="text-xl font-semibold text-white">Add Custom Glass Type</h2>
            <button
              onClick={toggleMenu}
              className="text-white hover:text-gray-300 focus:outline-none"
              aria-label="Close menu"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white mb-1">Name</label>
              <input
                type="text"
                value={newGlassType.name ?? ''}
                onChange={e => setNewGlassType(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 rounded-md border border-[#4b5563] bg-[#1e293b] text-base text-white focus:outline-none focus:ring-2 focus:ring-[#60a5fa] focus:border-transparent hover:bg-[#243447] hover:border-[#60a5fa] transition-colors duration-200"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">Type</label>
              <select
                value={newGlassType.type ?? ''}
                onChange={e => setNewGlassType(prev => ({ ...prev, type: e.target.value }))}
                className="w-full px-3 py-2 rounded-md border border-[#4b5563] bg-[#1e293b] text-base text-white focus:outline-none focus:ring-2 focus:ring-[#60a5fa] focus:border-transparent hover:bg-[#243447] hover:border-[#60a5fa] transition-colors duration-200"
                required
              >
                <option value="">Select a type</option>
                {allUniqueTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="thickness" className="text-white">Thickness</Label>
              {isCustomThickness ? (
                <Input
                  id="thickness"
                  type="text"
                  placeholder="Enter thickness"
                  value={newGlassType.thickness ?? 0}
                  onChange={e => setNewGlassType(prev => ({ ...prev, thickness: parseFloat(e.target.value) || 0 }))}
                  className="bg-[#1e293b] text-white border-[#4b5563] hover:bg-[#243447] hover:border-[#60a5fa] focus:ring-[#60a5fa] transition-colors duration-200 text-base"
                />
              ) : (
                <Select
                  value={selectedThicknessStr}
                  onValueChange={(value) => {
                    if (value === 'Other') {
                      setIsCustomThickness(true);
                      setNewGlassType(prev => ({ ...prev, thickness: 0 }));
                      setSelectedThicknessStr('');
                    } else {
                      // Find the matching thickness option
                      const option = thicknessOptions.find(opt => opt.display === value);
                      if (option) {
                        setNewGlassType(prev => ({ ...prev, thickness: option.value }));
                        setSelectedThicknessStr(value);
                      }
                    }
                  }}
                  onOpenChange={(open) => {
                    // When the select opens, add a click event listener to prevent propagation
                    if (open) {
                      setTimeout(() => {
                        const selectContent = document.querySelector('[data-radix-select-content]');
                        if (selectContent) {
                          selectContent.addEventListener('click', (e) => e.stopPropagation(), { capture: true });
                        }
                      }, 0);
                    }
                  }}
                >
                  <SelectTrigger className="bg-[#1e293b] text-white border-[#4b5563] hover:bg-[#243447] hover:border-[#60a5fa] focus:ring-[#60a5fa] transition-colors duration-200 text-base">
                    <SelectValue placeholder="Select thickness" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1e293b] text-white border-[#4b5563]">
                    {thicknessOptions.map((option) => (
                      <SelectItem key={option.display} value={option.display}>
                        {option.display}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {isCustomThickness && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsCustomThickness(false);
                    setNewGlassType(prev => ({ ...prev, thickness: 0 }));
                    setSelectedThicknessStr('');
                  }}
                  className="mt-1 text-xs text-white border-[#4b5563] hover:bg-[#243447] hover:border-[#60a5fa]"
                >
                  Back to standard options
                </Button>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">Weight (lbs/sq ft)</label>
              <input
                type="number"
                step="0.01"
                value={newGlassType.lbsPerSqFt ?? 0}
                onChange={e => setNewGlassType(prev => ({ ...prev, lbsPerSqFt: parseFloat(e.target.value) || 0 }))}
                className="w-full px-3 py-2 rounded-md border border-[#4b5563] bg-[#1e293b] text-base text-white focus:outline-none focus:ring-2 focus:ring-[#60a5fa] focus:border-transparent hover:bg-[#243447] hover:border-[#60a5fa] transition-colors duration-200"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">Price</label>
              <input
                type="number"
                step="0.01"
                value={newGlassType.price ?? 0}
                onChange={e => setNewGlassType(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                className="w-full px-3 py-2 rounded-md border border-[#4b5563] bg-[#1e293b] text-base text-white focus:outline-none focus:ring-2 focus:ring-[#60a5fa] focus:border-transparent hover:bg-[#243447] hover:border-[#60a5fa] transition-colors duration-200"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 mt-2 mb-4"
            >
              Add Glass Type
            </button>
          </form>
        </div>
      </div>

      <div className="mb-4">
        <h2 className="text-xl font-semibold mb-4 text-white">Filter Glass Types</h2>
        <div className="flex flex-wrap gap-2">
          {ALL_GLASS_TYPES.map(type => (
            <button
              key={type}
              onClick={() => handleTypeSelect(type)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                selectedType === type
                  ? 'bg-blue-600 text-white border-2 border-blue-600'
                  : 'bg-[#1e293b] text-white hover:bg-[#243447] border border-[#4b5563]'
              }`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="flex flex-wrap items-center mb-4">
          <h2 className="text-xl font-semibold text-white mr-4">
            {selectedType ? `${selectedType} Glass Types` : `${activeView === 'default' ? 'Default' : 'Custom'} Glass Types`}
          </h2>

          {/* Thickness filter buttons for Monolithic glass */}
          {selectedType === 'Monolithic' && (
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-sm text-white">Filter by thickness:</span>
              {['1/8"', '1/4"', '3/8"', '1/2"'].map(thickness => (
                <button
                  key={thickness}
                  onClick={() => handleThicknessSelect(thickness)}
                  className={`px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                    selectedThicknessFilter === thickness
                      ? 'bg-blue-600 text-white border-2 border-blue-600'
                      : 'bg-[#1e293b] text-white hover:bg-[#243447] border border-[#4b5563]'
                  }`}
                >
                  {thickness}
                </button>
              ))}
              {selectedThicknessFilter && (
                <button
                  onClick={() => setSelectedThicknessFilter('')}
                  className="px-2 py-1 rounded-md text-xs font-medium bg-gray-600 text-white hover:bg-gray-700 border border-gray-600"
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredGlassTypes.map(type => (
            <div
              key={type.id}
              className="border rounded-lg p-4 shadow-sm hover:shadow-md transition-all bg-[#1e293b] border-[#4b5563] cursor-pointer hover:bg-[#243447] relative group"
              onClick={() => handleGlassTypeClick(type)}
            >
              {/* Info icon indicator */}
              <div className="absolute top-2 right-2 text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
              </div>
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-lg font-medium text-white">{type.name}</h3>
                {type.source === 'custom' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent card click event
                      removeCustomGlassType(type.id);
                    }}
                    className="text-red-400 hover:text-red-300 text-sm font-medium mr-6"
                  >
                    Remove
                  </button>
                )}
              </div>
              <div className="space-y-1 text-sm text-white">
                <p><span className="font-medium">Type:</span> {type.type}</p>
                <p><span className="font-medium">Thickness:</span> {type.thicknessStr || formatThickness(type.thickness)}</p>
                <p><span className="font-medium">Weight:</span> {type.lbsPerSqFt} lbs/sq ft</p>
                <p><span className="font-medium">Price:</span> {formatCurrency(type.price)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Glass Type Modal */}
      <GlassTypeModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        glassType={selectedGlassType}
      />
    </div>
  );
};
